import { FormEvent, useState } from 'react'
import { useAppAuth } from '../../app/hooks/UseAppAuth.ts'
import { useAppDispatch } from '../../app/ReduxHooks.ts'
import { useSetFavoriteClubMutation } from '../../features/SpeechAgendaApiSlice.ts'
import { showGlobalModal } from '../../features/GlobalNotificationModalSlice.ts'
import { ClubUser } from '../../generated'

const NO_FAVORITE = ''

export default function FavoriteClubSection() {
  const { user } = useAppAuth()
  const clubs = user?.clubs ?? []
  if (clubs.length < 2) {
    return null
  }
  const favoriteId = clubs.find((club) => club.favorite)?.id
  // Keyed on the saved favorite so the select starts again from it after every save
  return (
    <FavoriteClubForm
      key={favoriteId ?? NO_FAVORITE}
      clubs={clubs}
      favoriteId={favoriteId}
    />
  )
}

function FavoriteClubForm({
  clubs,
  favoriteId,
}: {
  clubs: ClubUser[]
  favoriteId?: number
}) {
  const dispatch = useAppDispatch()
  const [selected, setSelected] = useState(
    favoriteId?.toString() ?? NO_FAVORITE
  )
  const [setFavoriteClub, { isLoading }] = useSetFavoriteClubMutation()
  const clubsByName = [...clubs].sort((a, b) => a.name.localeCompare(b.name))

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const club = clubs.find((c) => c.id.toString() === selected)
    let result: { isSuccess: boolean; title: string; description: string }
    try {
      await setFavoriteClub(club ? { clubId: club.id } : {}).unwrap()
      result = {
        isSuccess: true,
        title: 'Favorite club saved',
        description: club
          ? `${club.name} now opens when you sign in.`
          : 'You have no favorite club. The first of your clubs by name opens when you sign in.',
      }
    } catch {
      result = {
        isSuccess: false,
        title: 'Favorite club not saved',
        description: 'Something went wrong. Please try again.',
      }
    }
    dispatch(showGlobalModal({ ...result, isOpen: true, buttonText: 'Close' }))
  }

  return (
    <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 py-16 md:grid-cols-3">
      <div>
        <h2 className="text-base font-semibold leading-7">Favorite club</h2>
        <p className="mt-1 text-sm leading-6 text-gray-600">
          The club that opens when you sign in
        </p>
      </div>
      <div className="md:col-span-2">
        <form onSubmit={handleSubmit}>
          <div className="sm:max-w-xl">
            <label
              htmlFor="favorite-club"
              className="block text-sm font-medium leading-6 text-gray-900">
              Club
            </label>
            <select
              id="favorite-club"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6">
              <option value={NO_FAVORITE}>No favorite</option>
              {clubsByName.map((club) => (
                <option key={club.id} value={club.id.toString()}>
                  {club.name}
                </option>
              ))}
            </select>
          </div>
          <div className="mt-8 flex items-center">
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
              Save favorite club
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
